/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Table, Page } from '../../design';
import { useConfirmation, useRequester } from '../../hooks';
import { useContext, useEffect, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ScenarioResource } from '../../resources';
import { AuthContext } from '../../contexts';
import { Searchbar } from '../../design';

type Query = {
  offset: number;
  limit: number;
  qs?: string;
};

interface Row {
  id: string;
  title: string;
  description: string;
}

const columns = [
  { id: 'title', label: 'Title' },
  { id: 'description', label: 'Description' },
];

const toRow = (ob: ScenarioResource): Row => {
  const data = ob.data;

  return {
    id: data.id,
    title: data.title,
    description: data.description,
  };
};

export default function ScenariosPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [query, setQuery] = useState<Query>({ offset: 0, limit: 50 });

  const deleteConfirmation = useConfirmation<Row>({
    getTitle: (scenario: Row) => `Delete scenario ${scenario.title}?`,
    getDescription: (scenario: Row) =>
      `You are about to delete ${scenario.title}. This action cannot be undone.`,
    onConfirm: async (scenario: Row) => {
      await ScenarioResource.deleteOne(scenario.id);
    },
  });

  const scenariosApi = useRequester<ScenarioResource[]>({
    onSubmit: (q) => ScenarioResource.fetchMany(q),
    onSuccess: (data) => setHasReachedBottom(data.length < query.limit),
    onError: console.log,
  });

  useEffect(() => {
    scenariosApi.submit(query);
  }, [query.offset]);

  const rows = ScenarioResource.list().map(toRow);

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full">
          <Typography variant="body1" color="text.secondary" sx={{ mt: 8 }}>
            👋 Hey, {user?.data.firstName}!
          </Typography>
          <Typography variant="h1">Scenarios</Typography>
          <Box className="flex-grow"></Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box className="flex justify-between mt-8 mb-8">
          <Box className="flex gap-4 flex-grow">
            <Searchbar disabled placeholder="Search for a scenario" onChange={(qs) => { }} />
            {scenariosApi.loading && (
              <Box className="flex items-center">
                <CircularProgress size={20} />
              </Box>
            )}
            <Box className="flex-grow"></Box>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={() => navigate('/scenarios/new')}
            >
              New Scenario
            </Button>
          </Box>
        </Box>

        {scenariosApi.error ? (
          <Alert severity="error">{scenariosApi.error}</Alert>
        ) : (
          <>
            <Table
              // @ts-ignore
              columns={columns}
              rows={rows}
              onRowClick={(row) => navigate(`/scenarios/${row.id}`)}
              onEdit={(row) => navigate(`/scenarios/${row.id}/edit`)}
              onDelete={(row) => deleteConfirmation.triggerFor(row as Row)}
            />
            {!hasReachedBottom && rows.length > 0 && (
              <Button
                disabled={scenariosApi.loading}
                sx={{ mt: 2 }}
                fullWidth
                onClick={() =>
                  setQuery({ ...query, offset: query.offset + query.limit })
                }
              >
                Load More
              </Button>
            )}
          </>
        )}
      </Page.Content>
    </Page>
  );
}
